import { useForm, router } from '@inertiajs/react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import type { PageProps } from '@/types'

interface LoginProps extends PageProps {
  show_quick_login?: boolean
}

export default function Login({ flash, show_quick_login }: LoginProps) {
  const [showPassword, setShowPassword] = useState(false)

  const { data, setData, post, processing } = useForm({
    email_address: '',
    password: '',
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post('/session')
  }

  return (
    <div className="flex h-screen items-center justify-center bg-muted">
      <Card className="w-[400px] rounded-2xl border shadow-none">
        <CardContent className="flex flex-col items-center gap-7 px-[44px] py-10">
          {/* Logo */}
          <div className="flex flex-col items-center gap-2">
            <img src="/nereus-logo.svg" alt="Nereus" className="h-12 w-12 rounded-[10px]" />
            <h1 className="text-2xl font-bold text-primary" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Nereus
            </h1>
            <p className="text-sm text-muted-foreground">
              Freight forwarding, powered by AI
            </p>
          </div>

          {/* Error message */}
          {flash?.alert && (
            <p className="text-sm text-destructive">{flash.alert}</p>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex w-full flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email" className="text-[13px] font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={data.email_address}
                onChange={(e) => setData('email_address', e.target.value)}
                autoComplete="email"
                autoFocus
                className="h-auto px-3.5 py-2.5"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password" className="text-[13px] font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={data.password}
                  onChange={(e) => setData('password', e.target.value)}
                  autoComplete="current-password"
                  className="h-auto px-3.5 py-2.5"
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" className="h-auto w-full py-3 px-5 text-sm font-semibold" disabled={processing}>
              Sign In
            </Button>
          </form>

          {/* Quick login buttons (non-production only) */}
          {show_quick_login && (
            <div className="flex w-full flex-col gap-2">
              <p className="text-[11px] text-center text-muted-foreground">Quick sign in</p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs cursor-pointer"
                  disabled={processing}
                  onClick={() => {
                    router.post('/session', { email_address: 'super@nereus.ai', password: 'asdasd' })
                  }}
                >
                  Super Admin
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs cursor-pointer"
                  disabled={processing}
                  onClick={() => {
                    router.post('/session', { email_address: 'sarah@xport.com', password: 'asdasd' })
                  }}
                >
                  XPORT Staff
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
